/**
 * Reviews Component
 *
 * Display listing reviews with pagination.
 */

import React from 'react';
import { useReviews } from '../hooks/useReviews';

interface ReviewsProps {
  listingId: string;
}

export const Reviews: React.FC<ReviewsProps> = ({ listingId }) => {
  const { reviews, isLoading } = useReviews(listingId);

  if (isLoading) {
    return (
      <section className="py-6 border-b border-gray-200">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-6" />
          <div className="grid md:grid-cols-2 gap-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full" />
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-24" />
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </div>
                </div>
                <div className="h-16 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!reviews || reviews.items.length === 0) {
    return (
      <section className="py-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Reviews</h2>
        <p className="text-gray-500">No reviews yet</p>
      </section>
    );
  }

  return (
    <section className="py-6 border-b border-gray-200">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <StarIcon className="w-5 h-5 fill-current text-gray-900" />
        <span className="text-xl font-semibold">
          {reviews.averageRating.toFixed(1)}
        </span>
        <span className="text-xl font-semibold">·</span>
        <span className="text-xl font-semibold">
          {reviews.totalCount} review{reviews.totalCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Reviews Grid */}
      <div className="grid md:grid-cols-2 gap-8">
        {reviews.items.slice(0, 6).map((review) => (
          <article key={review.id} className="space-y-3">
            <div className="flex items-center gap-3">
              {review.guest.photoURL ? (
                <img
                  src={review.guest.photoURL}
                  alt={review.guest.displayName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-sm font-semibold text-gray-500">
                    {review.guest.displayName.charAt(0)}
                  </span>
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900">
                  {review.guest.displayName}
                </p>
                <p className="text-sm text-gray-500">
                  {formatDate(review.createdAt)}
                </p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed">{review.content}</p>
          </article>
        ))}
      </div>

      {/* Show All Button */}
      {reviews.totalCount > 6 && (
        <button className="mt-6 px-6 py-3 border border-gray-900 rounded-lg font-medium hover:bg-gray-50 transition-colors">
          Show all {reviews.totalCount} reviews
        </button>
      )}
    </section>
  );
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months !== 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
}

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

export default Reviews;
